/**
 * The page's two catalogues and the one formatter that reads them (Sprint 103.0).
 *
 * The messages are ICU, formatted by the platform's `IcuMessageFormatter`, so a
 * plural here follows the same rules as in the editor. The editor's full
 * Localization Service is not used: it loads catalogues as package resources,
 * which a page with two inline catalogues has no need for.
 */

import { IcuMessageFormatter, negotiateLanguage, type TranslationParams } from '@archisimple/i18n';
import en from './en.json';
import fr from './fr.json';

export const LANGUAGES = ['fr', 'en'] as const;
export type Language = (typeof LANGUAGES)[number];
export type MessageKey = keyof typeof en;

const CATALOGUES: Record<Language, Record<MessageKey, string>> = { en, fr };
const formatter = new IcuMessageFormatter();

export type Translate = (key: MessageKey, params?: TranslationParams) => string;

export function translator(language: Language): Translate {
  const catalogue = CATALOGUES[language];
  return (key, params = {}) => {
    const message = catalogue[key] ?? CATALOGUES.en[key] ?? key;
    try {
      return formatter.format(message, params, { locale: language, key });
    } catch {
      // A broken message degrades to its text rather than taking the page down;
      // the catalogue test is what keeps this branch unreached.
      return message;
    }
  };
}

/** The reader's language among the two, English when neither is preferred. */
export function initialLanguage(preferred: readonly string[], stored: string | null): Language {
  if (stored === 'fr' || stored === 'en') return stored;
  const negotiated = negotiateLanguage(preferred, [...LANGUAGES], 'en');
  return negotiated === 'fr' ? 'fr' : 'en';
}
